import { computed, ref, type ComputedRef, type Ref } from 'vue';
import type { ChatStatusUpdate } from '@/api';

export interface ChatStatusDetail {
  id: number;
  text: string;
}

export interface ChatStatusGroup {
  id: string;
  label: string;
  details: ChatStatusDetail[];
  done: boolean;
}

export interface UseChatStatusGroupsApi {
  /** All groups of the current run, in arrival order. */
  groups: Ref<ChatStatusGroup[]>;
  /** Groups still running (no `group_end` yet). */
  openGroups: ComputedRef<ChatStatusGroup[]>;
  handleStatus: (update: ChatStatusUpdate) => void;
  clear: () => void;
}

/** Details are a floating window — only the most recent few stay visible. */
const MAX_VISIBLE_DETAILS = 3;
const FALLBACK_LABEL = 'Working…';

/**
 * Ephemeral state machine for `chat_status` websocket frames (see frontend.md /
 * PY-346). Groups are keyed by `group_id` — never by arrival order, since
 * parallel tool calls may interleave their frames. The backend intentionally
 * sends no closing frames at run end, so the owner must call `clear()` on run
 * boundaries (start, interrupt, cancel); nothing here is ever persisted.
 */
export function useChatStatusGroups(): UseChatStatusGroupsApi {
  const groups = ref<ChatStatusGroup[]>([]);
  const openGroups = computed(() => groups.value.filter(group => !group.done));
  let nextDetailId = 0;

  function findGroup(groupId: string): ChatStatusGroup | undefined {
    return groups.value.find(group => group.id === groupId);
  }

  function handleStatus(update: ChatStatusUpdate): void {
    const label = update.label?.trim() ?? '';
    switch (update.kind) {
      case 'group_start': {
        const existing = findGroup(update.group_id);
        if (existing) {
          // Duplicate start for a known group — treat as a re-open/rename.
          if (label) existing.label = label;
          existing.done = false;
          return;
        }
        groups.value.push({
          id: update.group_id,
          label: label || FALLBACK_LABEL,
          details: [],
          done: false,
        });
        return;
      }
      case 'detail': {
        if (!label) return;
        let group = findGroup(update.group_id);
        if (group?.done) return; // stale detail after the group closed
        if (!group) {
          // Detail for a group we never saw open — surface it anyway.
          group = { id: update.group_id, label: FALLBACK_LABEL, details: [], done: false };
          groups.value.push(group);
        }
        group.details.push({ id: nextDetailId++, text: label });
        while (group.details.length > MAX_VISIBLE_DETAILS) {
          group.details.shift();
        }
        return;
      }
      case 'group_end': {
        const group = findGroup(update.group_id);
        if (!group) return;
        group.done = true;
        group.details = [];
        return;
      }
    }
  }

  function clear(): void {
    groups.value = [];
  }

  return { groups, openGroups, handleStatus, clear };
}
